import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createScopedStorage } from "./workspaceStore";
import { getNewsStorageKey } from "../workspace/constants";

/**
 * News/Priority Messages Store
 * Manages real-time news notifications from the server
 *
 * Message Types:
 * - critical: Modal dialog, requires acknowledgment, resent until acknowledged
 * - warning: Toast notification (10s), sent once
 * - info: Toast notification (5s), sent once
 * - announcement: Persistent banner, dismissible
 */
export const useNewsStore = create(
  persist(
    (set, get) => ({
      // Active messages (not yet dismissed/acknowledged)
      messages: [],

      // Set of dismissed message IDs (persisted to avoid re-showing)
      dismissedMessageIds: new Set(),

      /**
       * Add a new message from the server
       * @param {Object} message - Message object from SignalR
       */
      addMessage: (message) => {
        const state = get();

        // Skip if already dismissed
        if (state.dismissedMessageIds.has(message.Id)) {
          console.log(
            `[News] Skipping already dismissed message ${message.Id}`,
          );
          return;
        }

        // Check if message already exists
        const exists = state.messages.find((m) => m.Id === message.Id);
        if (exists) {
          console.log(`[News] Message ${message.Id} already in queue`);
          return;
        }

        console.log(
          `[News] Adding message ${message.Id} (type: ${message.Type})`,
        );

        // Add to messages
        set({ messages: [...state.messages, message] });

        // Auto-dismiss based on type (if not requiring acknowledgment)
        if (message.Type === "info" && !message.RequiresAck) {
          setTimeout(() => {
            get().dismissMessage(message.Id);
          }, 5000);
        } else if (message.Type === "warning" && !message.RequiresAck) {
          setTimeout(() => {
            get().dismissMessage(message.Id);
          }, 10000);
        }
      },

      /**
       * Dismiss a message (remove from UI)
       * @param {string} messageId - Message ID to dismiss
       */
      dismissMessage: (messageId) => {
        const state = get();

        console.log(`[News] Dismissing message ${messageId}`);

        set({
          messages: state.messages.filter((m) => m.Id !== messageId),
          dismissedMessageIds: new Set([
            ...state.dismissedMessageIds,
            messageId,
          ]),
        });
      },

      /**
       * Acknowledge a critical message (sends to server + dismisses locally)
       * @param {string} messageId - Message ID to acknowledge
       * @param {Object} marketdataService - Reference to marketdata service
       */
      acknowledgeMessage: (messageId, marketdataService) => {
        const state = get();

        console.log(`[News] Acknowledging message ${messageId}`);

        // Send acknowledgment to server via SignalR
        if (marketdataService?.signalRConnection?.invoke) {
          marketdataService.signalRConnection
            .invoke("AcknowledgeMessage", messageId)
            .then(() => {
              console.log(`[News] Server acknowledged message ${messageId}`);
            })
            .catch((err) => {
              console.error(
                `[News] Failed to acknowledge message ${messageId}:`,
                err,
              );
            });
        }

        // Dismiss locally
        get().dismissMessage(messageId);
      },

      /**
       * Clear expired messages (called periodically by component)
       */
      clearExpired: () => {
        const state = get();
        const now = new Date();

        const validMessages = state.messages.filter((m) => {
          if (!m.ExpiresAt) return true; // No expiration
          return new Date(m.ExpiresAt) > now;
        });

        if (validMessages.length !== state.messages.length) {
          const expiredCount = state.messages.length - validMessages.length;
          console.log(`[News] Cleared ${expiredCount} expired messages`);
          set({ messages: validMessages });
        }
      },

      /**
       * Clear all messages (for testing/debugging)
       */
      clearAll: () => {
        console.log("[News] Clearing all messages");
        set({ messages: [], dismissedMessageIds: new Set() });
      },
    }),
    {
      name: "odin-news-storage", // Used as fallback name, actual key is dynamic (user-scoped)
      storage: createJSONStorage(() => createScopedStorage(getNewsStorageKey)),
      // Only persist dismissed IDs, not active messages
      partialize: (state) => ({
        dismissedMessageIds: Array.from(state.dismissedMessageIds),
      }),
      // Restore dismissed IDs from localStorage
      merge: (persistedState, currentState) => ({
        ...currentState,
        dismissedMessageIds: new Set(persistedState?.dismissedMessageIds || []),
      }),
    },
  ),
);

export default useNewsStore;
